import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Mail, X, Send, Paperclip, File, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

export const UniversalMessageModal = ({ item, type, onClose, onMessageSent }) => {
  // Déterminer le sujet par défaut selon le type
  const getDefaultSubject = () => {
    switch (type) {
      case 'rdv':
        return `Réponse à votre rendez-vous - ${item.device}`;
      case 'devis':
        return `Réponse à votre demande de devis - ${item.brand || ''} ${item.model || ''}`;
      case 'diagnostic':
        return `Réponse à votre demande de diagnostic - ${item.brand || ''} ${item.model || ''}`;
      default:
        return `Message de PHONELAB`;
    }
  };

  // Message par défaut selon le type
  const getDefaultMessage = () => {
    switch (type) {
      case 'rdv':
        return `Bonjour ${item.name},\n\nNous revenons vers vous concernant votre rendez-vous du ${item.date ? new Date(item.date).toLocaleDateString('fr-FR') : ''} à ${item.time || ''}.\n\nAppareil: ${item.device}\nService: ${item.service}\n\n[Votre message ici]\n\nCordialement,\nL'équipe PHONELAB\n📞 04 26 00 82 10`;
      case 'devis':
        return `Bonjour ${item.name},\n\nNous avons bien reçu votre demande de devis pour la réparation de votre ${item.brand || ''} ${item.model || ''} (${item.deviceType || ''}).\n\nProblème signalé: ${item.problem}\n\nNous vous proposons:\n\n[Insérez votre devis ici]\n\nCordialement,\nL'équipe PHONELAB\n📞 04 26 00 82 10`;
      case 'diagnostic':
        return `Bonjour ${item.name},\n\nNous avons bien reçu votre demande de diagnostic pour votre ${item.brand || ''} ${item.model || ''} (${item.deviceType || ''}).\n\nProblème signalé: ${item.problem}\n\n[Votre réponse ici]\n\nCordialement,\nL'équipe PHONELAB\n📞 04 26 00 82 10`;
      default:
        return `Bonjour ${item.name},\n\n[Votre message ici]\n\nCordialement,\nL'équipe PHONELAB`;
    }
  };

  const [recipientEmail, setRecipientEmail] = useState(item.email);
  const [subject, setSubject] = useState(getDefaultSubject());
  const [message, setMessage] = useState(getDefaultMessage());
  const [attachment, setAttachment] = useState(null);
  const [sending, setSending] = useState(false);
  const [generating, setGenerating] = useState(false);

  const generateWithAI = async () => {
    setGenerating(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${window.location.origin}/api/generate-email-reply`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ item_type: type, item_data: item })
      });
      if (!response.ok) throw new Error('Erreur génération IA');
      const data = await response.json();
      setMessage(data.suggestion);
      toast.success('✨ Message IA généré — vous pouvez le modifier avant envoi');
    } catch (err) {
      toast.error('❌ Erreur lors de la génération IA');
    } finally {
      setGenerating(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Vérifier la taille (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error('Le fichier est trop volumineux (max 10 MB)');
        return;
      }
      setAttachment(file);
      toast.success(`📎 Fichier "${file.name}" ajouté`);
    }
  };

  const removeAttachment = () => {
    setAttachment(null);
    toast.info('Fichier retiré');
  };

  const handleSend = async () => {
    if (!recipientEmail.trim() || !subject.trim() || !message.trim()) {
      toast.error('L\'email, le sujet et le message sont obligatoires');
      return;
    }

    // Validation email simple
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(recipientEmail)) {
      toast.error('Adresse email invalide');
      return;
    }

    setSending(true);

    try {
      const apiUrl = window.location.origin;
      const token = localStorage.getItem('admin_token');

      // Créer FormData pour envoyer le fichier
      const formData = new FormData();
      formData.append('recipient_email', recipientEmail);
      formData.append('subject', subject);
      formData.append('message', message);
      formData.append('item_type', type);
      if (attachment) {
        formData.append('attachment', attachment);
      }

      // Endpoint universel pour tous les types
      const response = await fetch(`${apiUrl}/api/send-universal-message/${item.id}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Erreur lors de l\'envoi du message');
      }

      toast.success('✅ Message envoyé avec succès !');
      if (onMessageSent) onMessageSent();
      onClose();
    } catch (error) {
      console.error('Erreur:', error);
      toast.error(`❌ ${error.message}`);
    } finally {
      setSending(false);
    }
  };

  const getTypeLabel = () => {
    switch (type) {
      case 'rdv': return 'Rendez-vous';
      case 'devis': return 'Devis';
      case 'diagnostic': return 'Diagnostic';
      default: return 'Message';
    }
  };

  const getInfoContent = () => {
    switch (type) {
      case 'rdv':
        return (
          <>
            <strong>Appareil :</strong> {item.device}<br />
            <strong>Service :</strong> {item.service}<br />
            <strong>Date :</strong> {item.date ? new Date(item.date).toLocaleDateString('fr-FR') : 'N/A'} à {item.time || 'N/A'}<br />
            <strong>Téléphone :</strong> {item.phone}
          </>
        );
      case 'devis':
        return (
          <>
            <strong>Appareil :</strong> {item.brand} {item.model} ({item.deviceType})<br />
            <strong>Problème :</strong> {item.problem}<br />
            <strong>Téléphone :</strong> {item.phone}
          </>
        );
      case 'diagnostic':
        return (
          <>
            <strong>Appareil :</strong> {item.brand} {item.model} ({item.deviceType})<br />
            <strong>Problème :</strong> {item.problem}<br />
            <strong>Téléphone :</strong> {item.phone}
          </>
        );
      default:
        return (
          <>
            <strong>Téléphone :</strong> {item.phone}
          </>
        );
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6 rounded-t-2xl flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <Mail size={24} />
            <div>
              <h2 className="text-2xl font-bold">Envoyer un message</h2>
              <p className="text-blue-100 text-sm">{getTypeLabel()} - {item.name}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="hover:bg-blue-800 p-2 rounded-full transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Form */}
        <div className="p-6 space-y-6">
          {/* Email destinataire (modifiable) */}
          <div>
            <Label htmlFor="recipient" className="text-lg font-semibold mb-2 flex items-center">
              📧 Destinataire
            </Label>
            <Input
              id="recipient"
              type="email"
              value={recipientEmail}
              onChange={(e) => setRecipientEmail(e.target.value)}
              placeholder="email@example.com"
              className="text-base"
            />
            <p className="text-xs text-gray-500 mt-1">Vous pouvez modifier l'adresse email si nécessaire</p>
          </div>

          {/* Subject */}
          <div>
            <Label htmlFor="subject" className="text-lg font-semibold mb-2">Sujet</Label>
            <Input
              id="subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Objet du message"
              className="text-base"
            />
          </div>

          {/* Message */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <Label htmlFor="message" className="text-lg font-semibold">Message</Label>
              <Button
                type="button"
                onClick={generateWithAI}
                disabled={generating || sending}
                className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white text-sm px-3 py-1 h-8"
              >
                {generating ? (
                  <>
                    <div className="animate-spin rounded-full h-3 w-3 border-2 border-white border-t-transparent" />
                    <span>Génération...</span>
                  </>
                ) : (
                  <>
                    <Sparkles size={14} />
                    <span>✨ Suggérer IA</span>
                  </>
                )}
              </Button>
            </div>
            <Textarea
              id="message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Votre message..."
              rows={12}
              className="text-base"
            />
          </div>

          {/* File Attachment */}
          <div>
            <Label className="text-lg font-semibold mb-2">Pièce jointe (optionnel)</Label>
            <div className="mt-2">
              {!attachment ? (
                <label className="flex items-center justify-center w-full px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-colors">
                  <Paperclip size={20} className="mr-2 text-gray-500" />
                  <span className="text-sm text-gray-600">Joindre un fichier (PDF, facture, devis, etc.) - Max 10 MB</span>
                  <input
                    type="file"
                    onChange={handleFileChange}
                    className="hidden"
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt"
                  />
                </label>
              ) : (
                <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <File size={20} className="text-green-600" />
                    <div>
                      <p className="text-sm font-medium text-green-900">{attachment.name}</p>
                      <p className="text-xs text-green-700">{(attachment.size / 1024).toFixed(2)} KB</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={removeAttachment}
                    className="text-red-600 hover:text-red-800"
                  >
                    <X size={20} />
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Client Info Reminder */}
          <div className="bg-gray-50 rounded-lg p-4 border-l-4 border-blue-500">
            <p className="text-sm font-semibold text-gray-700 mb-2">📋 Rappel de la demande :</p>
            <p className="text-sm text-gray-600">
              {getInfoContent()}
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 p-6 rounded-b-2xl flex justify-end space-x-3">
          <Button
            onClick={onClose}
            variant="outline"
            className="px-6"
            disabled={sending}
          >
            Annuler
          </Button>
          <Button
            onClick={handleSend}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 flex items-center space-x-2"
            disabled={sending}
          >
            {sending ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                <span>Envoi...</span>
              </>
            ) : (
              <>
                <Send size={18} />
                <span>Envoyer</span>
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};
